import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import style from './styles.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import {
    SubjectAssociationType,
    SubjectObservationType,
} from '@interfaces-clean/subject-monitoring/subject-observations';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromLocal, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { SubjectNotesApi } from '@apis/bff-api/subject-monitoring/subject-notes-api';

const NOTE_MAX_LENGTH = 3000;

export interface AddSubjectNoteDialogProps {
    sessionId?: string;
    studyRef: string;
    subjectId: number;
    subjectNumber?: string;
    datasetId: number;
    datasetOpenedOn: Date;
    reviewId?: number;
    reviewTitle?: string;
    reviewDatasetDate?: string;
    /** Association label displayed in the read-only field (e.g. "Subject Profile") */
    associationName: string;
    /** Association type enum value passed to the API */
    associationType: SubjectAssociationType;
    /** Data listing name — null when triggered from the Notes tab */
    dataListingName: string | null;
    /** Data listing ref — null when triggered from the Notes tab */
    dataListingRef: string | null;
    panelName: string | null;
    panelRef: string | null;
    onClose: () => void;
    onSubmit: () => void;
    /** Called when the server responds with a 403 / "no longer assigned" error */
    onRemovedFromReview: () => void;
}

interface AddSubjectNoteForm {
    note: string;
}

const defaultValues: AddSubjectNoteForm = {
    note: '',
};

const COMPONENT_NAME = 'add-subject-note-dialog';

const AddSubjectNoteDialog = ({
    sessionId,
    studyRef,
    subjectId,
    subjectNumber,
    datasetId,
    datasetOpenedOn,
    reviewId,
    reviewTitle,
    reviewDatasetDate,
    associationName,
    associationType,
    dataListingName,
    dataListingRef,
    panelName,
    panelRef,
    onClose,
    onSubmit,
    onRemovedFromReview,
}: AddSubjectNoteDialogProps) => {
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.addSubjectNoteDialog' });
    const { t: tCommon } = useTranslation('common');
    const { trigger: addNote, isMutating: isSaving } = SubjectNotesApi.useAdd(studyRef, sessionId);

    // Store this as a const - user could open dialog 23:59:59 and POST at 00:00:01
    const dateNow = useMemo(() => new Date(), []);

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
        reset,
    } = useForm<AddSubjectNoteForm>({ mode: 'all', defaultValues });

    useUnsavedChangesTracking({
        area: 'general',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'general',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        reset(defaultValues);
        onClose();
    };

    const isSaveDisabled = !isValid || !isDirty || isSaving || isSubmitting;

    const datasetDate = datasetOpenedOn ? (stringDateWithoutTimeFromLocal(datasetOpenedOn) ?? undefined) : undefined;

    const onSubmitHandler = async () =>
        handleSubmit(async (formData: AddSubjectNoteForm) => {
            try {
                const response = await addNote({
                    datasetId,
                    observationDate: dateNow.toISOString(),
                    observation: formData.note,
                    subjectId,
                    associationType,
                    associationName,
                    dataListingRef: dataListingRef ?? undefined,
                    dataListingName: dataListingName ?? undefined,
                    panelRef: panelRef ?? undefined,
                    panelName: panelName ?? undefined,
                    reviewId,
                    reviewTitle,
                    observationType: SubjectObservationType.Note,
                    dataSetDate: datasetDate,
                });

                if (response?.success) {
                    ShowSuccess(tCommon('actionsSuccessfulToast'));
                    reset(defaultValues);
                    onSubmit();
                } else if (response?.message === 'notAReviewerError') {
                    ShowError(t('notAReviewerError'));
                    onRemovedFromReview();
                }
            } catch (error: unknown) {
                const httpError = error as { status?: number };
                if (httpError?.status === 403) {
                    ShowError(t('notAReviewerError'));
                    onRemovedFromReview();
                } else {
                    ShowError(tCommon('errorMessage'));
                }
            }
        })();

    // The left label width needs to be a little wider if we're showing no data listing or panel
    const leftLabelClassName =
        !panelName && !dataListingName ? style.editObservationInfoLabelXShort : style.editObservationInfoLabelShort;

    return (
        <DraggableDialog
            dataTestId="add-subject-note-dialog"
            title={t('title')}
            onClose={onCloseHandler}
            onSubmit={onSubmitHandler}
            primaryBtnLabel={tCommon('save')}
            primaryBtnDisabled={isSaveDisabled}
            secondaryBtnLabel={tCommon('cancel')}
            secondaryBtnDisabled={isSaving || isSubmitting}
            initialWidth={552}
            initialHeight={455}
            bodyPadding="none"
        >
            <div className="k-w-full k-h-full k-d-flex-col">
                <div className={cn(style.editObservationInfo, 'k-pt-md k-px-md')}>
                    <div className={cn(style.divider, 'k-shrink-0 k-d-flex')}>
                        <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2">
                            <div className={cn(leftLabelClassName, 'k-font-medium')}>{t('fields.type')}</div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>Note</div>
                        </div>
                        <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2">
                            <div className={cn(style.editObservationInfoLabel, 'k-font-medium')}>
                                {t('fields.subjectNumber')}
                            </div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>{subjectNumber}</div>
                        </div>
                    </div>
                    <div className={cn(style.divider, 'k-shrink-0 k-d-flex')}>
                        <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2">
                            <div className={cn(leftLabelClassName, 'k-font-medium')}>{t('fields.dataset')}</div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>
                                {stringDateWithoutTimeFromLocal(datasetOpenedOn, 'DD-MMM-YYYY')}
                            </div>
                        </div>
                        <div className="k-flex k-justify-content-left k-flex-row k-d-flex !k-basis-1/2">
                            <div className={cn(style.editObservationInfoLabel, 'k-font-medium')}>Observation Date</div>
                            <div className={cn(style.editSiteObservationTopInfoDetailLabel)}>
                                {stringDateWithoutTimeFromUtc(dateNow, 'DD-MMM-YYYY')}
                            </div>
                        </div>
                    </div>
                    {dataListingName && (
                        <div className={cn('k-shrink-0 k-d-flex')}>
                            <div className="k-flex k-justify-content-left k-flex-row k-d-flex">
                                <div className={cn(leftLabelClassName, 'k-font-medium')}>
                                    {dataListingName && t('fields.dataListing')}
                                </div>
                                <div className={cn(style.editSiteObservationTopInfoDetailNoWidth)}>
                                    {dataListingName}
                                </div>
                            </div>
                        </div>
                    )}
                    {panelName && (
                        <div className={cn('k-shrink-0 k-d-flex')}>
                            <div className="k-flex k-justify-content-left k-flex-row k-d-flex">
                                <div className={cn(leftLabelClassName, 'k-font-medium')}>
                                    {panelName && t('fields.panel')}
                                </div>
                                <div className={cn(style.editSiteObservationTopInfoDetailNoWidth)}>{panelName}</div>
                            </div>
                        </div>
                    )}
                    {reviewTitle && (
                        <div className={cn('k-shrink-0 k-d-flex')}>
                            <div className="k-flex k-justify-content-left k-flex-row k-d-flex">
                                <div className={cn(leftLabelClassName, 'k-font-medium')}>{t('fields.review')}</div>
                                <div className={cn(style.editSiteObservationTopInfoDetailNoWidth)}>
                                    {reviewTitle} - {reviewDatasetDate}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <form
                    id="add-subject-note-form"
                    className="k-w-full k-d-flex k-flex-column k-gap-3"
                    style={{ padding: '8px 11px' }}
                >
                    <TextAreaController
                        control={control}
                        name="note"
                        data-testid="field-note"
                        label={`${t('fields.note')} *`}
                        maxLength={NOTE_MAX_LENGTH}
                        required
                        readOnly={isSaving || isSubmitting}
                        fillMode="solid"
                        className={cn(style.addNoteInput)}
                    />
                </form>
            </div>
        </DraggableDialog>
    );
};

export default AddSubjectNoteDialog;
